import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { Question } from "@/types/test";
import { useToast } from "@/hooks/use-toast";
import { QuestionSelectionList } from "./question-generation/QuestionSelectionList";
import { ActionButtons } from "./question-generation/ActionButtons";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingStateWithProgress } from "../test-details/states/LoadingStateWithProgress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { generateAdditionalQuestions } from "@/utils/additional-questions-api";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface SkillConfig {
  skill: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'any';
  counts: {
    multiple_choice: number;
    essay: number;
    audio_response: number;
    video_response: number;
    coding: number;
  };
}

interface GenerateAdditionalQuestionsDialogProps {
  testTitle: string;
  testDescription: string;
  keyResponsibilities?: string;
  yearsOfExperience?: string;
  existingSkills: string[];
  existingQuestions: Question[];
  onQuestionsGenerated: (questions: Question[]) => void;
  disabled?: boolean;
}

export const GenerateAdditionalQuestionsDialog = ({
  testTitle,
  testDescription,
  keyResponsibilities,
  yearsOfExperience,
  existingSkills,
  existingQuestions,
  onQuestionsGenerated,
  disabled = false,
}: GenerateAdditionalQuestionsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState(0);
  const [skillConfigs, setSkillConfigs] = useState<SkillConfig[]>([]);
  const [newSkill, setNewSkill] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [showDistributionForm, setShowDistributionForm] = useState(true);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Total questions count
  const totalQuestions = skillConfigs.reduce((sum, config) => {
    return sum + Object.values(config.counts).reduce((countSum, count) => countSum + count, 0);
  }, 0);

  const handleAddSkill = () => {
    if (!newSkill.trim()) return;
    
    if (skillConfigs.some(config => config.skill.toLowerCase() === newSkill.toLowerCase())) {
      toast({
        title: "Duplicate Skill",
        description: "This skill has already been added",
        variant: "destructive",
      });
      return;
    }

    setSkillConfigs([
      ...skillConfigs,
      {
        skill: newSkill.trim(),
        difficulty: 'any',
        counts: {
          multiple_choice: 0,
          essay: 0,
          audio_response: 0,
          video_response: 0,
          coding: 0,
        }
      }
    ]);
    setNewSkill("");
  };

  const handleRemoveSkill = (index: number) => {
    setSkillConfigs(skillConfigs.filter((_, i) => i !== index));
  };

  const handleDifficultyChange = (skillIndex: number, difficulty: 'easy' | 'medium' | 'hard' | 'any') => {
    const newConfigs = [...skillConfigs];
    newConfigs[skillIndex].difficulty = difficulty;
    setSkillConfigs(newConfigs);
  };

  const handleCountChange = (skillIndex: number, questionType: keyof SkillConfig['counts'], value: number) => {
    const newConfigs = [...skillConfigs];
    newConfigs[skillIndex].counts[questionType] = Math.max(0, value);
    setSkillConfigs(newConfigs);
  };

  const handleGenerate = async () => {
    if (skillConfigs.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please add at least one skill",
        variant: "destructive",
      });
      return;
    }

    if (totalQuestions === 0) {
      toast({
        title: "Missing Information",
        description: "Please specify at least one question to generate",
        variant: "destructive",
      });
      return;
    }

    if (totalQuestions > 15) {
      toast({
        title: "Too Many Questions",
        description: "Please limit the total number of questions to 15 or fewer",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setShowDistributionForm(false);
    setProgress(10);

    try {
      // Build consolidated distribution and skills list
      const skills = skillConfigs.map(c => c.skill);
      const distribution = skillConfigs.reduce((acc, config) => ({
        multiple_choice: acc.multiple_choice + config.counts.multiple_choice,
        essay: acc.essay + config.counts.essay,
        audio_response: acc.audio_response + config.counts.audio_response,
        video_response: acc.video_response + config.counts.video_response,
        coding: acc.coding + config.counts.coding,
      }), {
        multiple_choice: 0,
        essay: 0,
        audio_response: 0,
        video_response: 0,
        coding: 0,
      });

      // Get most common difficulty (excluding 'any')
      const difficulties = skillConfigs
        .map(c => c.difficulty)
        .filter(d => d !== 'any') as ('easy' | 'medium' | 'hard')[];
      const difficulty = difficulties.length > 0 ? difficulties[0] : undefined;

      const result = await generateAdditionalQuestions({
        skills,
        distribution,
        difficulty,
        additionalComments,
        testContext: {
          title: testTitle,
          description: testDescription,
          keyResponsibilities,
          yearsOfExperience,
          existingSkills,
        },
      });

      setProgress(100);
      
      if (result.success && result.questions.length > 0) {
        setGeneratedQuestions(result.questions);
        setSelectedQuestions(new Set(result.questions.map((_, i) => i)));
      } else {
        throw new Error(result.error || 'Failed to generate questions');
      }
    } catch (error: any) {
      console.error('Failed to generate questions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate additional questions. Please try again.",
        variant: "destructive",
      });
      setShowDistributionForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    const selected = generatedQuestions.filter((_, index) => selectedQuestions.has(index));
    
    if (selected.length === 0) {
      toast({
        title: "No Selection",
        description: "Please select at least one question",
        variant: "destructive",
      });
      return;
    }

    onQuestionsGenerated(selected);
    toast({
      title: "Success",
      description: `Added ${selected.length} questions to your test`,
    });
    handleClose();
  };

  const toggleQuestion = (index: number) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedQuestions(newSelected);
  };

  const handleRegenerate = () => {
    setGeneratedQuestions([]);
    setSelectedQuestions(new Set());
    setShowDistributionForm(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowDistributionForm(true);
    setGeneratedQuestions([]);
    setSelectedQuestions(new Set());
    setLoading(false);
    setSkillConfigs([]);
    setNewSkill("");
    setAdditionalComments("");
  };

  const questionTypes: Array<keyof SkillConfig['counts']> = [
    'multiple_choice', 'essay', 'video_response', 'audio_response', 'coding'
  ];
  
  const questionTypeLabels: Record<keyof SkillConfig['counts'], string> = {
    multiple_choice: 'MCQ',
    essay: 'Essay',
    video_response: 'Video',
    audio_response: 'Audio',
    coding: 'Code'
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          disabled={disabled}
          className={isMobile ? "w-full" : ""}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Generate Additional Questions
        </Button>
      </DialogTrigger>

      <DialogContent 
        className={cn(
          "bg-card border-none p-0 overflow-hidden",
          isMobile ? "w-[95vw] min-h-[32rem] max-h-[90vh]" : "w-[85vw] max-w-6xl min-h-[32rem] max-h-[90vh]"
        )}
      >
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="text-foreground text-lg sm:text-xl">Generate Additional Questions</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Configure question generation for specific skills to add to your test
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center flex-1 min-h-[20rem]">
            <LoadingStateWithProgress
              message="Generating additional questions..."
              progress={progress}
            />
          </div>
        ) : showDistributionForm && !generatedQuestions.length ? (
          <div className="flex flex-col flex-1 min-h-0">
            <ScrollArea className="flex-1 max-h-[calc(100vh-20rem)] overflow-auto">
              <div className="px-6 py-4 min-w-[48rem]">
                {/* Add Skill Section */}
                <div className="mb-6 space-y-3">
                  <Label className="text-base font-semibold">Add Skills</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter skill name..."
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button onClick={handleAddSkill} size="icon" variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Add skills you want to generate questions for, then configure their difficulty and question distribution below
                  </p>
                </div>

                {/* Skills Configuration Table */}
                {skillConfigs.length > 0 ? (
                  <>
                    <div className="rounded-md border mb-4">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="w-[12rem] text-sm font-medium">Skill</TableHead>
                            <TableHead className="w-[8rem] text-sm font-medium">Difficulty</TableHead>
                            {questionTypes.map(qType => (
                              <TableHead key={qType} className="w-[6rem] text-center text-sm font-medium">
                                {questionTypeLabels[qType]}
                              </TableHead>
                            ))}
                            <TableHead className="w-[4rem] text-center text-sm font-medium">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {skillConfigs.map((config, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">
                                <span className="text-sm">{config.skill}</span>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={config.difficulty}
                                  onValueChange={(value: 'easy' | 'medium' | 'hard' | 'any') => 
                                    handleDifficultyChange(index, value)
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm w-[7rem]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any</SelectItem>
                                    <SelectItem value="easy">Easy</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="hard">Hard</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              {questionTypes.map(qType => (
                                <TableCell key={qType} className="text-center">
                                  <div className="flex justify-center">
                                    <Input
                                      type="number"
                                      min="0"
                                      placeholder="0"
                                      value={config.counts[qType] === 0 ? '' : config.counts[qType]}
                                      onChange={(e) => handleCountChange(index, qType, parseInt(e.target.value) || 0)}
                                      className="h-8 w-16 text-center text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </div>
                                </TableCell>
                              ))}
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveSkill(index)}
                                  className="h-8 w-8"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Total Questions Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Total Questions:</span>
                        <Badge variant={totalQuestions > 15 ? "destructive" : "default"}>
                          {totalQuestions}
                        </Badge>
                        {totalQuestions > 15 && (
                          <span className="text-sm text-destructive">Maximum 15 questions allowed</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-sm">No skills added yet. Add skills above to get started.</p>
                  </div>
                )}

                {/* Additional Comments */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="comments" className="text-base font-semibold">Additional Comments (Optional)</Label>
                  <Input
                    id="comments"
                    placeholder="Any specific requirements or context for question generation..."
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={handleClose} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleGenerate} 
                    className="flex-1"
                    disabled={skillConfigs.length === 0 || totalQuestions === 0 || totalQuestions > 15}
                  >
                    Generate Questions
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </div>
        ) : (
          <ScrollArea className="flex-1 max-h-[calc(100vh-20rem)] overflow-auto">
            <div className="px-6 py-4 space-y-4">
              <QuestionSelectionList
                questions={generatedQuestions}
                selectedQuestions={selectedQuestions}
                onToggleQuestion={toggleQuestion}
              />
              <ActionButtons
                onRegenerateQuestions={handleRegenerate}
                onConfirmQuestions={handleConfirm}
                hasSelectedQuestions={selectedQuestions.size > 0}
              />
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
